// src/features/chat/components/MessageList.jsx

import React, { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import styles from '../styles/MessageList.module.css';

const NEAR_BOTTOM_PX = 300;

/**
 * Memoized message list component with infinite scroll pagination.
 *
 * Key features:
 * - Only re-renders when messages array changes
 * - IntersectionObserver for pagination at top
 * - Auto-scroll to bottom when near bottom and new messages arrive
 *
 * @param {Object} props
 * @param {Array} props.messages - Array of normalized messages
 * @param {boolean} props.hasMore - Whether more messages can be loaded
 * @param {boolean} props.loadingMore - Whether currently loading more
 * @param {Function} props.onLoadMore - Callback to load more messages
 * @param {Function} props.renderMessage - Render function for each message
 * @param {React.ReactNode} [props.children] - Additional content (e.g., loading, empty, pending)
 */
export const MessageList = React.memo(function MessageList({
  messages,
  hasMore,
  loadingMore,
  onLoadMore,
  renderMessage,
  children,
}) {
  const listRef = useRef(null);
  const topSentinelRef = useRef(null);
  const bottomRef = useRef(null);

  // --- 1) Keep latest values for the observer callback without re-creating it
  const latestRef = useRef({ hasMore, loadingMore, onLoadMore });
  latestRef.current.hasMore = hasMore;
  latestRef.current.loadingMore = loadingMore;
  latestRef.current.onLoadMore = onLoadMore;

  // --- 2) Track if initial scroll has happened
  const didInitialScrollRef = useRef(false);

  // --- 3) Store scroll metrics for prepend anchor
  const prependAnchorRef = useRef(null);

  // --- 4) Track previous last message id for append detection
  const prevLastIdRef = useRef(null);

  // --- 5) Wrap loadMore to encapsulate prepend policy
  const requestLoadMore = useCallback(() => {
    const el = listRef.current;
    if (!el) return;

    // In-flight gate: prevents duplicate calls before state updates
    if (prependAnchorRef.current) return;

    // Bootstrap guard: prevents loop before initial scroll
    if (!didInitialScrollRef.current) return;

    const { hasMore, loadingMore, onLoadMore } = latestRef.current;
    if (!hasMore || loadingMore) return;

    // Snapshot current viewport before prepending
    prependAnchorRef.current = {
      scrollHeight: el.scrollHeight,
      scrollTop: el.scrollTop,
    };

    onLoadMore?.();
  }, []);

  // Derive lastId for initial scroll and append detection
  const lastId = messages[messages.length - 1]?.id ?? null;

  // --- 6) Scroll to bottom once, AFTER the first page actually arrives
  useLayoutEffect(() => {
    if (didInitialScrollRef.current) return;
    if (!lastId) return; // no messages yet

    bottomRef.current?.scrollIntoView({ block: 'end' });
    didInitialScrollRef.current = true;

    // Prevent the auto-follow effect from treating initial load like an append
    prevLastIdRef.current = lastId;
  }, [lastId]);

  // --- 7) Preserve viewport when prepending older messages
  // After loadMore completes and DOM updates, restore the anchor.
  useLayoutEffect(() => {
    const el = listRef.current;
    const anchor = prependAnchorRef.current;
    if (!el || !anchor) return;

    // Wait until the loadMore request has finished.
    if (loadingMore) return;

    const delta = el.scrollHeight - anchor.scrollHeight;
    el.scrollTop = anchor.scrollTop + delta;

    prependAnchorRef.current = null;
  }, [messages.length, loadingMore]);

  // --- 8) Create IntersectionObserver ONCE
  useEffect(() => {
    const rootEl = listRef.current;
    const sentinel = topSentinelRef.current;
    if (!rootEl || !sentinel) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          requestLoadMore();
        }
      },
      {
        root: rootEl,
        rootMargin: '100px 0px 0px 0px',
        threshold: 0,
      }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [requestLoadMore]);

  // --- 9) Auto-follow bottom only for true appends (not prepends)
  useEffect(() => {
    const el = listRef.current;
    if (!el || !lastId) return;
    if (!didInitialScrollRef.current) return;

    const prevLastId = prevLastIdRef.current;
    prevLastIdRef.current = lastId;

    // If the last id didn't change, this render was likely a prepend/edit.
    // Don't auto-scroll for history loads.
    if (prevLastId && lastId === prevLastId) return;

    const nearBottom =
      el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX;

    if (nearBottom) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [lastId]);

  return (
    <div className={styles.list} ref={listRef}>
      {/* Top sentinel for infinite scroll */}
      <div ref={topSentinelRef} style={{ height: 1 }} />

      {/* Loading indicator */}
      {loadingMore && (
        <div className={styles.loadingMore}>
          <div className={styles.spinner} />
          <span>Loading...</span>
        </div>
      )}

      {/* Scroll indicator */}
      {hasMore && !loadingMore && (
        <div
          className={`${styles.scrollIndicator} ${styles.scrollIndicatorVisible}`}
        >
          Scroll up to load more
        </div>
      )}

      {/* Messages */}
      {messages.map(renderMessage)}

      {/* Additional content (pending bubble, etc.) */}
      {children}

      {/* Bottom anchor for auto-scroll */}
      <div ref={bottomRef} style={{ height: 1 }} />
    </div>
  );
});

export default MessageList;
